import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Copy, ExternalLink, Sparkles, CreditCard, Gift, Award, TrendingUp, Apple, Smartphone, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth, api } from '../context/AuthContext';
import { RenewalConsent } from '../components/RenewalConsent';

// -------------------------------------------------------------- helpers
const STATUS_MAP = {
  trialing: { label: 'Free trial', tone: 'bg-brand-gold/20 text-brand-gold' },
  active: { label: 'Active', tone: 'bg-brand-green text-brand-cream' },
  canceled: { label: 'Canceled', tone: 'bg-red-100 text-red-700' },
  expired: { label: 'Expired', tone: 'bg-red-100 text-red-700' },
  none: { label: 'No subscription', tone: 'bg-brand-cream text-brand-green/70' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

function centsToUsd(c) {
  const n = Number(c || 0) / 100;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}


// ---------------------------------------- Signup social-proof tile
// Public GET /api/billing/signup-stats — this-month counts split by
// `signup_source` (ios / android / website). Renders as a small tile at
// the top of the billing page so visitors see the platform's momentum
// before they subscribe. Fails silently if the endpoint isn't reachable
// (no scary error banner blocking checkout).
function SignupStatsTile() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/billing/signup-stats');
        if (!cancelled) setStats(data);
      } catch {
        /* fail silently — this is a social-proof widget, not critical UX */
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!stats || stats.total === 0) return null;
  return (
    <div
      data-testid="billing-signup-stats-tile"
      className="rounded-xl p-5 bg-gradient-to-br from-brand-green to-brand-green/85 text-brand-cream shadow-sm"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 mt-1 text-brand-gold" />
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-brand-cream/70">
              {stats.month_label}
            </div>
            <div className="mt-1 font-serif text-2xl">
              <span data-testid="signup-stats-total">{stats.total.toLocaleString()}</span>{' '}
              {stats.total === 1 ? 'realtor' : 'realtors'} joined this month
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5" data-testid="signup-stats-ios">
            <Apple className="w-4 h-4 text-brand-gold" />
            <span className="font-semibold">{stats.ios.toLocaleString()}</span>
            <span className="text-brand-cream/70 text-xs">iOS</span>
          </div>
          <div className="flex items-center gap-1.5" data-testid="signup-stats-android">
            <Smartphone className="w-4 h-4 text-brand-gold" />
            <span className="font-semibold">{stats.android.toLocaleString()}</span>
            <span className="text-brand-cream/70 text-xs">Android</span>
          </div>
          <div className="flex items-center gap-1.5" data-testid="signup-stats-website">
            <Globe className="w-4 h-4 text-brand-gold" />
            <span className="font-semibold">{stats.website.toLocaleString()}</span>
            <span className="text-brand-cream/70 text-xs">Web</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Status card
function StatusCard({ me }) {
  const s = STATUS_MAP[me.subscription_status] || STATUS_MAP.none;
  const provider = me.subscription_provider
    ? me.subscription_provider === 'stripe' ? 'Stripe (web)' : 'Apple (iOS)'
    : '—';
  return (
    <div data-testid="billing-status-card" className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-brand-green/60">Current plan</div>
          <div className="mt-1 font-serif text-2xl text-brand-green">LISTING AiGENT Realtor</div>
          <div className="text-sm text-brand-green/70">$10 for 7 days · then $100 / month</div>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${s.tone}`} data-testid="billing-status-badge">
          {s.label}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5 text-sm">
        <div>
          <div className="text-brand-green/60">Provider</div>
          <div className="text-brand-green font-medium">{provider}</div>
        </div>
        <div>
          <div className="text-brand-green/60">Trial ends</div>
          <div className="text-brand-green font-medium">{fmtDate(me.trial_end)}</div>
        </div>
        <div>
          <div className="text-brand-green/60">Renews</div>
          <div className="text-brand-green font-medium">{fmtDate(me.current_period_end)}</div>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------- Trial CTA card
function TrialCtaCard({ hasSubscription, busy, onStart, onManage, consent, setConsent }) {
  if (hasSubscription) {
    return (
      <div data-testid="billing-manage-card" className="bg-brand-green text-brand-cream rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <CreditCard className="w-6 h-6 text-brand-gold shrink-0" />
          <div>
            <div className="font-serif text-xl">Manage your subscription</div>
            <p className="mt-1 text-sm text-brand-cream/80">
              Update your card, download invoices, or cancel — all in the Stripe billing portal.
            </p>
            <Button className="mt-4 bg-brand-gold text-brand-green hover:bg-brand-gold/90"
                    onClick={onManage} data-testid="billing-manage-btn">
              Open billing portal <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="billing-start-card" className="bg-brand-green text-brand-cream rounded-xl p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <Sparkles className="w-6 h-6 text-brand-gold shrink-0" />
        <div>
          <div className="font-serif text-2xl">Start your 7-day trial</div>
          <p className="mt-1 text-sm text-brand-cream/80">
            $10 up front, then $100/month. Cancel anytime during the trial with no monthly charge.
          </p>
          <ul className="mt-3 space-y-1 text-sm text-brand-cream/85">
            {['Unlimited listings', 'AI Call Intake & 24/7 phone assistant',
              'Buyer-match blasts + Open House RSVPs', 'Google Calendar sync']
              .map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
          </ul>
          <RenewalConsent checked={consent} onChange={setConsent} tone="dark" />
          <Button className="mt-5 bg-brand-gold text-brand-green hover:bg-brand-gold/90"
                  onClick={onStart} disabled={busy || !consent} data-testid="billing-start-btn">
            {busy ? 'Preparing checkout…' : 'Start trial → checkout'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------- Referral card
function ReferralCard({ code, shareUrl, rewardCents, creditCents, onCopy }) {
  return (
    <div data-testid="billing-referral-card" className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-2">
        <Gift className="w-5 h-5 text-brand-gold" />
        <h2 className="font-serif text-xl text-brand-green">Share &amp; earn {centsToUsd(rewardCents)}</h2>
      </div>
      <p className="mt-2 text-sm text-brand-green/70">
        Send your code to another realtor. When they finish their trial and pay their first monthly bill, you both get {centsToUsd(rewardCents)} credited.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] font-mono text-lg tracking-widest text-brand-green bg-brand-cream rounded-lg px-4 py-3 text-center" data-testid="referral-code">
          {code || '—'}
        </div>
        <Button variant="outline" onClick={() => onCopy(code, 'code')} data-testid="referral-copy-code-btn">
          <Copy className="w-4 h-4 mr-2" /> Copy code
        </Button>
        <Button variant="outline" onClick={() => onCopy(shareUrl, 'link')} data-testid="referral-copy-link-btn">
          <Copy className="w-4 h-4 mr-2" /> Copy share link
        </Button>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-brand-cream pt-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-brand-green/60">Your credit balance</div>
          <div className="mt-1 font-serif text-2xl text-brand-green" data-testid="referral-credit-balance">
            {centsToUsd(creditCents)}
          </div>
        </div>
        <span className="text-xs text-brand-green/60">Auto-applied to your next bill</span>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- Page
// -------------------------------------------------------- Redeem credits
function RedeemCard({ offer, busy, onRedeemWeb, needsConsent }) {
  if (!offer || !offer.eligible) {
    return (
      <div data-testid="billing-redeem-card" className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-brand-gold" />
          <h2 className="font-serif text-xl text-brand-green">Redeem referral credits</h2>
        </div>
        <p className="mt-2 text-sm text-brand-green/70">
          You have <strong>{offer?.available_count || 0}</strong> qualified referral{(offer?.available_count === 1) ? '' : 's'}.
          Refer 1 more realtor to unlock your first tier ($25 off next bill).
        </p>
      </div>
    );
  }
  const label = offer.is_free_month ? 'one free month' : `$${offer.discount_amount} off`;
  const isApple = offer.iap_platform === 'apple';
  return (
    <div data-testid="billing-redeem-card" className="bg-white rounded-xl p-6 shadow-sm border border-brand-gold/30">
      <div className="flex items-center gap-2">
        <Award className="w-5 h-5 text-brand-gold" />
        <h2 className="font-serif text-xl text-brand-green">Redeem — {label}</h2>
      </div>
      <p className="mt-2 text-sm text-brand-green/70">
        You have <strong>{offer.available_count}</strong> qualified referrals — tier {offer.tier} unlocked.
        {offer.carry_over > 0 && ` We'll carry ${offer.carry_over} to your next redemption.`}
      </p>
      {isApple ? (
        <div className="mt-4 text-xs text-brand-green/70 bg-brand-cream rounded-lg p-3">
          Open the LISTING AiGENT iOS app → Billing → Redeem credits. Your Apple subscription will apply {label} at renewal.
        </div>
      ) : (
        <>
          {needsConsent && (
            <p data-testid="billing-redeem-consent-hint" className="mt-3 text-xs text-brand-green/70">
              Agree to the automatic renewal terms in the trial card above to continue.
            </p>
          )}
          <Button className="mt-4 w-full sm:w-auto" onClick={onRedeemWeb} disabled={busy || needsConsent}
                  data-testid="billing-redeem-btn">
            {busy ? 'Preparing checkout…' : `Apply ${label} at checkout`}
          </Button>
        </>
      )}
    </div>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(user);
  const [referral, setReferral] = useState({ code: '', share_url: '', reward_cents: 5000 });
  const [offer, setOffer] = useState(null);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [{ data: fresh }, { data: ref }, { data: off }] = await Promise.all([
          api.get('/auth/me'),
          api.get('/referrals/my-code'),
          api.get('/referral/offer'),
        ]);
        if (!mounted) return;
        setMe(fresh);
        setReferral(ref);
        setOffer(off);
      } catch (err) {
        toast({ title: 'Could not load billing status', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSubscription = me && ['trialing', 'active'].includes(me.subscription_status);

  const startTrial = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/billing/checkout', { renewal_consent: consent });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast({ title: 'Checkout failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [toast, consent]);

  const openPortal = useCallback(async () => {
    try {
      const { data } = await api.get('/billing/portal');
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (err) {
      toast({ title: 'Portal unavailable', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  }, [toast]);

  const copy = useCallback(async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `Copied ${label}` });
    } catch {
      toast({ title: 'Copy failed', description: 'Long-press to select and copy manually.', variant: 'destructive' });
    }
  }, [toast]);

  if (!me) {
    return (
      <div className="min-h-screen bg-brand-cream/40 py-16 text-center text-brand-green/60" data-testid="billing-loading">
        Loading your plan…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream/40" data-testid="billing-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 space-y-6">
        <header>
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Billing</span>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-green">Your plan &amp; credits</h1>
          <p className="mt-2 text-brand-green/70 text-sm">
            Manage your subscription and share your invite code — synced with the LISTING AiGENT mobile app.
          </p>
        </header>

        <SignupStatsTile />
        <StatusCard me={me} />
        <TrialCtaCard hasSubscription={hasSubscription} busy={busy}
                      onStart={startTrial} onManage={openPortal}
                      consent={consent} setConsent={setConsent} />
        <ReferralCard code={referral.code} shareUrl={referral.share_url}
                      rewardCents={referral.reward_cents}
                      creditCents={me.credit_balance_cents}
                      onCopy={copy} />
        <RedeemCard offer={offer} busy={busy} onRedeemWeb={startTrial}
                    needsConsent={!hasSubscription && !consent} />
      </div>
    </div>
  );
}
